import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowDown, ArrowUp, Hash } from "lucide-react";
import { fetchGridDetail } from "@/lib/api";

const fmtTime = (ms) => {
    if (!ms) return "--";
    const d = new Date(ms);
    return d.toLocaleTimeString("en-US", { hour12: false }) + "." + String(d.getMilliseconds()).padStart(3, "0");
};
const fmtDateTime = (ms) => {
    if (!ms) return "--";
    const d = new Date(ms);
    return d.toLocaleString("en-US", { hour12: false });
};

const EVENT_COLOR = {
    PLACED: "#3DA7FF",
    OPEN: "#3DA7FF",
    FILLED: "#2BE38B",
    PARTIALLY_FILLED: "#F0B94A",
    CANCELED: "#A7B4C6",
    STALE: "#6F7D92",
    RECOVERED: "#2EE6D6",
    REJECTED: "#FF5C6C",
};

// Compact fill-progress bar for partially-filled orders
const FillProgressBar = ({ pct, side }) => {
    const color = side === "BUY" ? "#2BE38B" : "#FF5C6C";
    const safe = Math.max(0, Math.min(100, Number(pct || 0)));
    return (
        <div
            className="mt-1 h-1.5 rounded-full overflow-hidden"
            style={{ background: "rgba(167,180,198,0.14)" }}
            data-testid={`fill-progress-${side?.toLowerCase()}`}
        >
            <div
                className="h-full transition-[width] duration-300"
                style={{ width: `${safe}%`, background: color }}
            />
        </div>
    );
};

export const GridDetailDialog = ({ gridId, open, onOpenChange, fetcher }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const fetchFn = fetcher || fetchGridDetail;

    useEffect(() => {
        if (!open || !gridId) return;
        let alive = true;
        const fetch = async () => {
            setLoading(true);
            try {
                const d = await fetchFn(gridId);
                if (alive) setDetail(d);
            } catch (_) { /* ignore */ }
            finally { if (alive) setLoading(false); }
        };
        fetch();
        const id = setInterval(fetch, 3000);
        return () => { alive = false; clearInterval(id); };
    }, [open, gridId, fetchFn]);

    if (!gridId) return null;
    const events = (detail?.events || []).slice().sort((a, b) => a.ts_ms - b.ts_ms);
    const trades = detail?.trades || [];
    const realized = Number(detail?.realized_pnl || 0);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="panel max-w-3xl" data-testid="grid-detail-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Hash size={16} color="#2EE6D6" />
                        <span className="font-mono">{gridId}</span>
                        <span
                            className="ml-2 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded"
                            style={{
                                color: "#2EE6D6", background: "rgba(46,230,214,0.10)",
                                border: "1px solid rgba(46,230,214,0.25)",
                            }}
                            data-testid="grid-detail-state"
                        >
                            {detail?.state || (loading ? "loading…" : "unknown")}
                        </span>
                    </DialogTitle>
                    <DialogDescription>
                        Immutable event timeline for this grid (clientOrderId pair).
                    </DialogDescription>
                </DialogHeader>

                {/* Current state */}
                <div className="grid grid-cols-3 gap-3 my-2">
                    <div className="p-3 rounded-md"
                         style={{ background: "rgba(43,227,139,0.06)", border: "1px solid rgba(43,227,139,0.18)" }}>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Active BUY</div>
                        {detail?.active_buy ? (
                            <>
                                <div className="text-sm font-mono text-[#2BE38B] mt-0.5">
                                    ${Number(detail.active_buy.price).toFixed(2)} · {detail.active_buy.qty} BTC
                                </div>
                                {Number(detail.active_buy.cumulative_filled_qty) > 0 && (
                                    <>
                                        <div className="text-[10px] text-[#A7B4C6] mt-1 font-mono">
                                            filled {Number(detail.active_buy.cumulative_filled_qty).toFixed(6)} BTC
                                            <span className="text-[#F0B94A]"> ({Number(detail.active_buy.fill_pct).toFixed(1)}%)</span>
                                        </div>
                                        {Number(detail.active_buy.avg_fill_price) > 0 && (
                                            <div className="text-[10px] text-[#6F7D92] font-mono">
                                                avg ${Number(detail.active_buy.avg_fill_price).toFixed(2)}
                                            </div>
                                        )}
                                        <FillProgressBar pct={detail.active_buy.fill_pct} side="BUY" />
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="text-sm text-[#3A4759] mt-0.5">—</div>
                        )}
                    </div>
                    <div className="p-3 rounded-md"
                         style={{ background: "rgba(255,92,108,0.06)", border: "1px solid rgba(255,92,108,0.18)" }}>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Active SELL</div>
                        {detail?.active_sell ? (
                            <>
                                <div className="text-sm font-mono text-[#FF5C6C] mt-0.5">
                                    ${Number(detail.active_sell.price).toFixed(2)} · {detail.active_sell.qty} BTC
                                    <div className="text-[10px] text-[#6F7D92]">
                                        (buy@${Number(detail.active_sell.buy_price).toFixed(2)})
                                    </div>
                                </div>
                                {Number(detail.active_sell.cumulative_filled_qty) > 0 && (
                                    <>
                                        <div className="text-[10px] text-[#A7B4C6] mt-1 font-mono">
                                            filled {Number(detail.active_sell.cumulative_filled_qty).toFixed(6)} BTC
                                            <span className="text-[#F0B94A]"> ({Number(detail.active_sell.fill_pct).toFixed(1)}%)</span>
                                        </div>
                                        {Number(detail.active_sell.avg_fill_price) > 0 && (
                                            <div className="text-[10px] text-[#6F7D92] font-mono">
                                                avg ${Number(detail.active_sell.avg_fill_price).toFixed(2)}
                                            </div>
                                        )}
                                        <FillProgressBar pct={detail.active_sell.fill_pct} side="SELL" />
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="text-sm text-[#3A4759] mt-0.5">—</div>
                        )}
                    </div>
                    <div className="p-3 rounded-md"
                         style={{ background: "rgba(46,230,214,0.06)", border: "1px solid rgba(46,230,214,0.18)" }}>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Realized PnL</div>
                        <div
                            className="text-lg font-mono mt-0.5"
                            style={{ color: realized >= 0 ? "#2BE38B" : "#FF5C6C" }}
                            data-testid="grid-detail-pnl"
                        >
                            {realized >= 0 ? "+" : ""}${realized.toFixed(4)}
                        </div>
                    </div>
                </div>

                {/* Trade fills */}
                {trades.length > 0 && (
                    <div className="my-2">
                        <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6] mb-1">Fills</div>
                        <div className="space-y-1">
                            {trades.map((t, i) => {
                                const isBuy = t.side === "BUY";
                                return (
                                    <div key={`${t.filled_at_ms}-${t.side}-${i}`} className="flex items-center gap-2 font-mono text-xs">
                                        <span style={{ color: "#6F7D92" }}>{fmtDateTime(t.filled_at_ms)}</span>
                                        <span style={{ color: isBuy ? "#2BE38B" : "#FF5C6C" }}>
                                            {isBuy ? <ArrowUp size={10} className="inline" /> : <ArrowDown size={10} className="inline" />}
                                            {" "}{t.side}
                                        </span>
                                        <span style={{ color: "#E6EDF6" }}>${Number(t.price).toFixed(2)}</span>
                                        <span style={{ color: "#A7B4C6" }}>{t.qty}</span>
                                        {t.side === "SELL" && (
                                            <span style={{ color: Number(t.realized_pnl) >= 0 ? "#2BE38B" : "#FF5C6C" }}>
                                                {Number(t.realized_pnl) >= 0 ? "+" : ""}${Number(t.realized_pnl).toFixed(4)}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Event timeline */}
                <div className="my-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6] mb-1">
                        Event Timeline (immutable)
                    </div>
                    <div className="max-h-[280px] overflow-y-auto log-scroll rounded-md"
                         style={{ background: "#05070A", border: "1px solid #1B2A3A" }}
                         data-testid="grid-detail-events">
                        {events.length === 0 && (
                            <div className="text-[11px] text-[#3A4759] p-4 text-center">
                                {loading ? "loading…" : "No recorded events for this grid."}
                            </div>
                        )}
                        {events.map((e, i) => (
                            <div key={`${e.ts_ms}-${e.event}-${i}`} className="flex gap-3 px-3 py-1.5 border-b border-[#13202D] last:border-b-0 font-mono text-xs">
                                <span style={{ color: "#3A4759", minWidth: 110 }}>{fmtTime(e.ts_ms)}</span>
                                <span
                                    style={{
                                        color: EVENT_COLOR[e.event] || "#A7B4C6",
                                        minWidth: 80, textTransform: "uppercase",
                                        letterSpacing: "0.08em",
                                    }}
                                >
                                    {e.event || e.status}
                                </span>
                                <span style={{ color: e.side === "BUY" ? "#2BE38B" : "#FF5C6C", minWidth: 36 }}>
                                    {e.side}
                                </span>
                                <span style={{ color: "#E6EDF6" }}>${Number(e.price).toFixed(2)}</span>
                                <span style={{ color: "#A7B4C6" }}>{e.qty}</span>
                                <span className="truncate" style={{ color: "#6F7D92" }}>{e.client_order_id}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default GridDetailDialog;
